import { genderT } from '@/types'
import { httpClient } from './httpClient'

interface AddAndAssignPatient {
  productId: string
  firstName: string
  lastName: string
  gender: genderT
  dateOfBirth: string
  username: string
  password: string
  lang: string
  phoneNumber?: string
  otherContact?: string
  fiscalCode?: string
}

type AssignPatientResponse = {
  data: {
    patientId: number
  }
}

type AssignPatientPayload = {
  email: string
}

export async function assignPatient ({ email }: AssignPatientPayload) {
  try {
    let url = import.meta.env.VITE_DOCTOR_MANAGE_PATIENT_ENDPOINT
    url = url.replace('__login__', email)

    const response: AssignPatientResponse = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url,
      withCredentials: true,
      method: 'POST'
    })

    return response?.data
  } catch (error) {
    console.error('assignPatient - Error: ', error)
    throw error
  }
}

export async function addAndAssignPatient ({
  productId,
  firstName,
  lastName,
  gender,
  dateOfBirth,
  username,
  password,
  lang,
  phoneNumber,
  otherContact,
  fiscalCode
}: AddAndAssignPatient) {
  try {
    const patient = {
      firstName,
      lastName,
      gender,
      lang,
      dateOfBirth,
      ...(phoneNumber ? { phoneNumber } : {}),
      ...(otherContact ? { otherContact } : {})
    }

    const payload = {
      patient,
      credentials: {
        login: username,
        password
      },
      productId,
      fiscalInfo: fiscalCode ? { fiscalCode } : undefined
    }

    const response: AssignPatientResponse = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url: import.meta.env.VITE_CREATE_PATIENT_ENDPOINT,
      withCredentials: true,
      method: 'POST',
      data: payload
    })

    if (!response) {
      throw Error
    }

    return response
  } catch (error) {
    console.error('addAndAssignPatient - Error: ', error)
    throw error
  }
}

export async function renewTrainingProgram ({
  patientId,
  productId
}: {
  patientId: string
  productId: string
}) {
  try {
    const payload = {
      productId
    }
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url: `/Doctor/RenewPatientsPayment/${patientId}`,
      withCredentials: true,
      method: 'POST',
      data: payload
    })

    if (!response) {
      throw Error
    }

    return response?.data
  } catch (error) {
    console.error('renewTrainingProgram - Error: ', error)
    throw error
  }
}

export type PatientNotes = {
  notes: string | null
  updatedAt: string | null
}

export async function getPatientNotes ({ patientId }: { patientId: string }) {
  try {
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url: `/Doctor/Patient/${patientId}/Notes`,
      withCredentials: true,
      method: 'GET',
      cache: false
    })

    return response?.data as PatientNotes
  } catch (error) {
    console.error('getPatientNotes - Error: ', error)
    throw error
  }
}

export async function updatePatientNotes ({
  patientId,
  notes
}: {
  patientId: string
  notes: string
}) {
  try {
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url: `/Doctor/Patient/${patientId}/Notes`,
      withCredentials: true,
      method: 'PUT',
      data: { notes }
    })

    return response?.data as PatientNotes
  } catch (error) {
    console.error('updatePatientNotes - Error: ', error)
    throw error
  }
}

export async function getPatientData () {
  try {
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_PATIENT_BASEURL,
      url: import.meta.env.VITE_USER_DATA_ENDPOINT,
      withCredentials: true,
      method: 'GET',
      cache: false
    })

    return response?.data
  } catch (error) {
    console.error('getPatientData - Error: ', error)
    throw error
  }
}

export async function getPatientSettings () {
  try {
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_PATIENT_BASEURL,
      url: import.meta.env.VITE_USER_SETTINGS_ENDPOINT,
      withCredentials: true,
      method: 'GET',
      cache: false
    })

    return response?.data
  } catch (error) {
    console.error('getPatientSettings - Error: ', error)
    throw error
  }
}
